import React, { useEffect, useRef } from "react";
import { TextInput, Dropdown, Sidepanel } from "pepsico-ds";
import { User } from "../../../../types/users";
import { Controller, useForm } from "react-hook-form";
import { schema } from "../../../../schema/users";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppSelector, useAppDispatch } from "../../../../hooks/app";
import {
  createUser,
  updateUser,
} from "../../../../redux/actions/users/userAction";
import "./AddEditUser.css";
import { toast } from "react-toastify";
import { ROLES } from "../../../../config/roles";

interface Props {
  isAdd: boolean;
  onCloseModal: () => void;
  showModal: boolean;
  editUser: User | undefined;
  reloadFunction: () => void;
}

const AddEditUser = (props: Props) => {
  const dispatch = useAppDispatch();
  const {
    userRoles: activeUserRoles,
    userSectors: activeUserSectors,
    hasAdmin,
  } = useAppSelector((state) => state.userAccess.data);
  const { roles, sectors } = useAppSelector((state) => state.appData);
  const [nonGpLeadRoles, setNonGpLeadRoles] = React.useState([]);
  const [nonGpLeadSectors, setNonGpLeadSectors] = React.useState([]);
  const formRef = useRef<HTMLFormElement>(null);
  const {
    watch,
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      gpid: "",
      firstName: "",
      lastName: "",
      email: "",
      userRoles: [],
      userSectors: [],
    },
    resolver: zodResolver(schema),
  });
  const { isAdd, onCloseModal, showModal, editUser, reloadFunction } = props;
  const closeHandler = () => {
    resetData();
    onCloseModal();
  };
  const onSubmit = handleSubmit(async (data) => {
    const roles = [ROLES.basicUser.displayText, ROLES.admin.displayText];
    const isAdminOrBasicUser = data.userRoles.some((role) =>
      roles.includes(role.displayText)
    );
    if (isAdd) {
      try {
        await dispatch(
          createUser({
            ...data,
            userSectors: isAdminOrBasicUser ? [] : data.userSectors,
          })
        ).unwrap();
        toast.success("User created successfully");
        reloadFunction();
        closeHandler();
      } catch (err) {
        console.log(err);
        toast.error(err as string);
      }
    } else {
      const payload = {
        ...data,
        userRoles: [...data.userRoles],
        userSectors: isAdminOrBasicUser
          ? []
          : [...data.userSectors, ...nonGpLeadSectors],
      };
      try {
        //@ts-ignore
        await dispatch(updateUser(payload)).unwrap();
        toast.success("User updated successfully");
        reloadFunction();
        closeHandler();
      } catch (err) {
        console.log(err);
        toast.error(err as string);
      }
    }
  });
  const resetData = () => {
    reset({}, { keepErrors: false, keepDirty: false });
    setValue("gpid", "");
    setValue("firstName", "");
    setValue("lastName", "");
    setValue("email", "");
    //@ts-ignore
    setValue("userRoles", []);
    setValue("userSectors", []);
  };

  useEffect(() => {
    if (showModal) {
      if (!isAdd) {
        if (editUser) {
          if (!hasAdmin) {
            // these are roles allowed for GP Lead
            const gpLeadAllowedRoles = [
              ROLES.gpLead.displayText,
              ROLES.basicUser.displayText,
            ];
            const _nonGpLeadRoles = editUser.userRoles.filter(
              (role) => !gpLeadAllowedRoles.includes(role.displayText)
            );
            const _gpLeadRole = editUser.userRoles.filter((role) =>
              gpLeadAllowedRoles.includes(role.displayText)
            );

            // create a contsant _nonGpLeadSectors to filters the sectors that are not GP Lead
            const _nonGpLeadSectors = editUser.userSectors.filter(
              (sector) =>
                !activeUserSectors.some(
                  (activeSector) =>
                    activeSector.sectors.name === sector.displayText
                )
            );
            const _gpLeadSector = editUser.userSectors.filter((sector) =>
              activeUserSectors.some(
                (activeSector) =>
                  activeSector.sectors.name === sector.displayText
              )
            );
            //@ts-ignore
            setNonGpLeadRoles(_nonGpLeadRoles);
            //@ts-ignore
            setNonGpLeadSectors(_nonGpLeadSectors);
            //@ts-ignore
            setValue("userRoles", _gpLeadRole);
            setValue("userSectors", _gpLeadSector);
          } else {
            //@ts-ignore
            setValue("userRoles", editUser.userRoles);
            setValue("userSectors", editUser.userSectors);
          }
          setValue("gpid", editUser.gpid);
          setValue("firstName", editUser.firstName);
          setValue("lastName", editUser.lastName);
          setValue("email", editUser.email);
        }
      }
    }
  }, [
    showModal,
    isAdd,
    editUser,
    setValue,
    hasAdmin,
    activeUserRoles,
    activeUserSectors,
  ]);

  const submitHandler = () => {
    formRef.current?.dispatchEvent(
      new Event("submit", { cancelable: true, bubbles: true })
    );
  };

  const watchRoles = watch("userRoles");
  const sectorRoles = [ROLES.stLead.displayText, ROLES.gpLead.displayText];
  const hasAdminOrBasicUser = watchRoles?.filter((role) =>
    sectorRoles.includes(role.displayText)
  ).length;

  return (
    <Sidepanel
      title={isAdd ? "Add User" : "Edit User"}
      showTertiaryButton={false}
      showLink={false}
      showSecondaryButton={false}
      showSidePanel={showModal}
      onCloseSidePanel={closeHandler}
      primaryButtonProps={{
        text: "Save",
        type: "submit",
        onClick: submitHandler,
      }}
    >
      <form onSubmit={onSubmit} ref={formRef}>
        <div>
          <Controller
            control={control}
            {...register("gpid")}
            render={({ field }) => (
              <TextInput
                label="GPID *"
                placeholder="Enter GPID"
                error={errors.gpid?.message}
                value={field.value}
                onUpdate={field.onChange}
                disabled={!isAdd}
              />
            )}
          />
          <Controller
            control={control}
            {...register("firstName")}
            render={({ field }) => (
              <TextInput
                label="First Name *"
                placeholder="Enter First Name"
                error={errors.firstName?.message}
                value={field.value}
                onUpdate={field.onChange}
              />
            )}
          />
          <Controller
            control={control}
            {...register("lastName")}
            render={({ field }) => (
              <TextInput
                label="Last Name *"
                placeholder="Enter Last Name"
                error={errors.lastName?.message}
                value={field.value}
                onUpdate={field.onChange}
              />
            )}
          />
          <Controller
            control={control}
            {...register("email")}
            render={({ field }) => (
              <TextInput
                label="Email *"
                placeholder="Enter Email"
                error={errors.email?.message}
                value={field.value}
                onUpdate={field.onChange}
                disabled={!isAdd}
              />
            )}
          />
          <Controller
            control={control}
            {...register("userRoles")}
            render={({ field }) => (
              <div className="add-edit-user-dropdown">
                <Dropdown
                  style={{ marginBottom: 10, marginTop: 10, width: "100%" }}
                  label="Roles"
                  selectedValue={
                    field.value.length > 0 ? field.value : undefined
                  }
                  setSelectedValue={field.onChange}
                  childList={roles}
                  error={!!errors.userRoles?.message}
                  helperText={errors.userRoles?.message}
                />
              </div>
            )}
          />
          {hasAdminOrBasicUser ? (
            <Controller
              control={control}
              {...register("userSectors")}
              render={({ field }) => (
                <div className="add-edit-user-dropdown">
                  <Dropdown
                    selection="multiple"
                    style={{ marginBottom: 10, marginTop: 10, width: "100%" }}
                    label="Sectors"
                    selectedValue={
                      field.value.length > 0 ? field.value : undefined
                    }
                    setSelectedValue={field.onChange}
                    childList={sectors}
                    error={!!errors.userSectors?.message}
                    helperText={errors.userSectors?.message}
                  />
                </div>
              )}
            />
          ) : null}
        </div>
      </form>
    </Sidepanel>
  );
};
export default AddEditUser;
